import { Dispatch, SetStateAction } from 'react';
import { ChevronLeft, ChevronRight } from '@drift-labs/icons';
import { Dayjs } from 'dayjs';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import { cj } from 'src/utils/classNames';
import { WithClassName } from 'src/utils/UtilityTypes';

interface Props {
	monthIndex: number;
	setMonthIndex: Dispatch<SetStateAction<number>>;
	lastThreeMonths: Dayjs[];
	isLoading: boolean;
	numWinningDays: number;
	numLosingDays: number;
	numFlatDays: number;
}

export default function PortfolioTradeCalendarHeader({
	monthIndex,
	setMonthIndex,
	lastThreeMonths,
	isLoading,
	numWinningDays,
	numLosingDays,
	numFlatDays,
}: Props) {
	return (
		<>
			<div
				className={cj(
					'flex items-center justify-between',
					isLoading && 'animate-pulse'
				)}
			>
				{/* Desktop month selector */}
				<MonthSelector
					className="hidden md:flex"
					monthIndex={monthIndex}
					setMonthIndex={setMonthIndex}
					lastThreeMonths={lastThreeMonths}
				/>

				<div className="flex items-center gap-x-lg">
					<DailyStat
						label="Winning Days"
						value={numWinningDays}
						isLoading={isLoading}
					/>
					<DailyStat
						label="Losing Days"
						value={numLosingDays}
						isLoading={isLoading}
					/>
					<DailyStat
						label="Flat Days"
						value={numFlatDays}
						isLoading={isLoading}
					/>
				</div>
			</div>

			{/* Mobile month selector */}
			<MonthSelector
				className="flex self-center md:hidden"
				monthIndex={monthIndex}
				setMonthIndex={setMonthIndex}
				lastThreeMonths={lastThreeMonths}
			/>
		</>
	);
}

interface MonthSelectorProps extends WithClassName {
	monthIndex: number;
	setMonthIndex: Dispatch<SetStateAction<number>>;
	lastThreeMonths: Dayjs[];
}

function MonthSelector({
	monthIndex,
	setMonthIndex,
	lastThreeMonths,
	className,
}: MonthSelectorProps) {
	return (
		<div
			className={cj(
				'items-center gap-x-sm typo-t3 text-text-default',
				className
			)}
		>
			<IconButton
				size="sm"
				icon={ChevronLeft}
				onClick={() => {
					setMonthIndex((i) => i + 1);
				}}
				disabled={monthIndex === lastThreeMonths.length - 1}
			/>
			<span className="min-w-18 text-center md:min-w-20">
				{lastThreeMonths[monthIndex].format('MMM YYYY')}
			</span>
			<IconButton
				size="sm"
				icon={ChevronRight}
				onClick={() => {
					setMonthIndex((i) => i - 1);
				}}
				disabled={monthIndex === 0}
			/>
		</div>
	);
}

interface DailyStatProps {
	label: string;
	value: number;
	isLoading: boolean;
}

function DailyStat({ label, value, isLoading }: DailyStatProps) {
	return (
		<div className="flex items-center gap-x-xs">
			<span className="typo-b4 text-text-secondary">{label}</span>
			<span className="min-w-xl typo-t3 text-text-default">
				{isLoading ? '-' : value}
			</span>
		</div>
	);
}
